import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Spin } from 'antd';
import { useAuthStore } from '@/store/authStore';
import MainLayout from '@/layouts/MainLayout';
import { color } from '@/theme/tokens';

// Lazy load pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const TimesheetsPage = lazy(() => import('@/pages/timesheets/TimesheetsPage'));
const TimesheetDetailPage = lazy(() => import('@/pages/timesheets/TimesheetDetailPage'));
const CreateTimesheetPage = lazy(() => import('@/pages/timesheets/CreateTimesheetPage'));
const ApprovalsPage = lazy(() => import('@/pages/approvals/ApprovalsPage'));
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'));
const UsersPage = lazy(() => import('@/pages/users/UsersPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const FinancialsPage = lazy(() => import('@/pages/financials/FinancialsPage'));
const PayrollCalculatorPage = lazy(() => import('@/pages/payroll-calculator/PayrollCalculatorPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const PtoPage = lazy(() => import('@/pages/pto/PtoPage'));

const PageLoader = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: color.canvas,
  }}>
    <Spin size="large" />
  </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const RoleRoute = ({ 
  children, 
  allowedRoles 
}: { 
  children: React.ReactNode;
  allowedRoles: string[];
}) => {
  const user = useAuthStore((state) => state.user);
  
  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
          } 
        />
        
        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          
          {/* Timesheets */}
          <Route path="timesheets" element={<TimesheetsPage />} />
          <Route path="pto" element={<PtoPage />} />
          {/* Separate keys: going from a new timesheet to an existing one starts the page fresh */}
          <Route path="timesheets/new" element={<CreateTimesheetPage key="new" />} />
          <Route path="timesheets/:id" element={<TimesheetDetailPage />} />
          <Route path="timesheets/:id/edit" element={<CreateTimesheetPage key="edit" />} />
          
          {/* Approvals - PM and Admin only */}
          <Route 
            path="approvals" 
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN', 'PROJECT_MANAGER']}>
                <ApprovalsPage />
              </RoleRoute>
            } 
          />
          
          {/* Projects - PM and Admin only */}
          <Route 
            path="projects" 
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN', 'PROJECT_MANAGER']}>
                <ProjectsPage />
              </RoleRoute>
            } 
          />
          
          {/* Users - PM and Admin only */}
          <Route 
            path="users" 
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN', 'PROJECT_MANAGER']}>
                <UsersPage />
              </RoleRoute>
            } 
          />
          
          {/* Reports - Admin only */}
          <Route 
            path="reports" 
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN']}>
                <ReportsPage />
              </RoleRoute>
            } 
          />
          
          {/* Financials - Admin only */}
          <Route
            path="financials"
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN']}>
                <FinancialsPage />
              </RoleRoute>
            }
          />

          {/* Payroll Calculator - PM and Admin only */}
          <Route
            path="payroll-calculator"
            element={
              <RoleRoute allowedRoles={['SUPER_ADMIN', 'PROJECT_MANAGER']}>
                <PayrollCalculatorPage />
              </RoleRoute>
            }
          />

          {/* Profile */}
          <Route path="profile" element={<ProfilePage />} />
        </Route>
        
        {/* Catch all */}
        <Route 
          path="*" 
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
          } 
        />
      </Routes>
    </Suspense>
  );
}

export default App;

